/**
 * In-game chat: message feed (bottom-left, fades out) + input opened
 * with Enter. System messages (join/leave) show in yellow.
 * Keystrokes inside the input are stopped from bubbling so they don't
 * trigger game hotkeys (abilities, hotbar, inventory).
 */

export type ChatMessage = {
  username: string;
  text: string;
  system?: boolean;
};

export type Chat = {
  addMessage: (message: ChatMessage) => void;
  isOpen: () => boolean;
  dispose: () => void;
};

const MAX_MESSAGES = 8;
const MESSAGE_TTL_MS = 10000;

export function createChat(options: {
  onSend: (text: string) => void;
  canOpen: () => boolean;
  /** Called after the input closes — reacquire pointer lock here. */
  onClose?: () => void;
}): Chat {
  let open = false;

  const feed = document.createElement("div");
  feed.id = "chat-feed";
  feed.style.cssText = `
    position: absolute;
    bottom: 96px;
    left: 12px;
    width: min(420px, 60vw);
    display: flex;
    flex-direction: column;
    gap: 2px;
    pointer-events: none;
    font-size: 13px;
    line-height: 1.35;
  `;

  const inputWrap = document.createElement("div");
  inputWrap.id = "chat-input-wrap";
  inputWrap.style.cssText = `
    position: absolute;
    bottom: 64px;
    left: 12px;
    width: min(420px, 60vw);
    display: none;
    pointer-events: auto;
  `;

  const input = document.createElement("input");
  input.type = "text";
  input.maxLength = 200;
  input.placeholder = "Mensagem… (Enter envia, Esc fecha)";
  input.style.cssText = `
    width: 100%;
    padding: 8px 10px;
    background: rgba(0, 0, 0, 0.7);
    border: 1px solid #555;
    border-radius: 6px;
    color: #fff;
    font-size: 13px;
    outline: none;
  `;
  inputWrap.appendChild(input);

  const overlay = document.getElementById("ui-overlay");
  (overlay ?? document.body).appendChild(feed);
  (overlay ?? document.body).appendChild(inputWrap);

  function addMessage(message: ChatMessage) {
    const line = document.createElement("div");
    line.style.cssText = `
      background: rgba(0, 0, 0, 0.45);
      padding: 3px 8px;
      border-radius: 4px;
      color: ${message.system ? "#ffe066" : "#fff"};
      text-shadow: 0 1px 2px rgba(0,0,0,0.8);
      transition: opacity 1s;
      word-break: break-word;
    `;
    line.textContent = message.system
      ? message.text
      : `<${message.username}> ${message.text}`;
    feed.appendChild(line);

    while (feed.children.length > MAX_MESSAGES) {
      feed.removeChild(feed.firstChild!);
    }

    setTimeout(() => {
      line.style.opacity = "0";
      setTimeout(() => line.remove(), 1100);
    }, MESSAGE_TTL_MS);
  }

  function openChat() {
    open = true;
    inputWrap.style.display = "block";
    document.exitPointerLock();
    // Focus after the pointer-lock exit settles
    setTimeout(() => input.focus(), 0);
  }

  function closeChat() {
    open = false;
    input.value = "";
    inputWrap.style.display = "none";
    input.blur();
    options.onClose?.();
  }

  function handleGlobalKeyDown(e: KeyboardEvent) {
    if (e.code === "Enter" && !open && options.canOpen()) {
      e.preventDefault();
      openChat();
    }
  }

  function handleInputKeyDown(e: KeyboardEvent) {
    // Keep game hotkeys (1-9, E, WASD…) from firing while typing
    e.stopPropagation();
    if (e.code === "Enter") {
      const text = input.value.trim();
      if (text) options.onSend(text);
      closeChat();
    } else if (e.code === "Escape") {
      closeChat();
    }
  }

  document.addEventListener("keydown", handleGlobalKeyDown);
  input.addEventListener("keydown", handleInputKeyDown);

  return {
    addMessage,
    isOpen: () => open,
    dispose() {
      document.removeEventListener("keydown", handleGlobalKeyDown);
      input.removeEventListener("keydown", handleInputKeyDown);
      feed.remove();
      inputWrap.remove();
    },
  };
}
